/**
 * @cfleet/auth - Local Auth Adapter
 *
 * Implements the AuthAdapter interface for local/direct API authentication.
 * This adapter makes direct API calls to cfleet-core-server endpoints
 * using bearer token authentication.
 *
 * Key characteristics:
 * - Bearer token-based authentication
 * - Tokens stored in localStorage/SecureStore
 * - Manual token refresh via refresh_token
 * - No CSRF required (token-based)
 *
 * This adapter is used for:
 * - Unit tests and journey tests
 * - Local development without Better Auth
 * - Backward compatibility with existing auth flow
 */

import type {
  AuthAdapter,
  AuthAdapterResponse,
  SessionResponse,
} from './types';
import type {
  LoginCredentials,
  User,
  AuthTokens,
  Organization,
  UserRole,
  UserPermissions,
  AppAuthConfig,
} from '../types';
import { DEFAULT_PERMISSIONS } from '../types';

/**
 * Normalize role string to UserRole type
 * Backend returns lowercase roles, frontend uses PascalCase
 */
function normalizeRole(role: string | undefined, appType: string): UserRole {
  if (!role) {
    // Default role based on app type
    switch (appType) {
      case 'driver':
        return 'Driver';
      case 'customer':
        return 'Customer';
      default:
        return 'Dispatcher';
    }
  }

  const roleMap: Record<string, UserRole> = {
    admin: 'Admin',
    manager: 'Manager',
    dispatcher: 'Dispatcher',
    viewer: 'Dispatcher', // Map viewer to Dispatcher
    driver: 'Driver',
    customer: 'Customer',
    // PascalCase passthrough
    Admin: 'Admin',
    Manager: 'Manager',
    Dispatcher: 'Dispatcher',
    Driver: 'Driver',
    Customer: 'Customer',
  };

  return roleMap[role] || roleMap[role.toLowerCase()] || 'Dispatcher';
}

/**
 * Normalize API response profile to User type
 */
function normalizeProfile(
  profile: Record<string, unknown>,
  appType: string
): User {
  const id = (profile.id as string) || '';
  const name = (profile.name as string) || '';
  const email = (profile.email as string) || '';
  const role = normalizeRole(profile.role as string | undefined, appType);

  // Handle avatar - driver uses avatar_url, others use avatar
  const avatar =
    (profile.avatar as string) || (profile.avatar_url as string) || undefined;

  // Handle organizations
  let organizations: Organization[] = [];
  if (profile.organizations && Array.isArray(profile.organizations)) {
    organizations = profile.organizations as Organization[];
  } else if (profile.organization_ids && Array.isArray(profile.organization_ids)) {
    organizations = (profile.organization_ids as string[]).map((orgId) => ({
      id: orgId,
      name: orgId,
      role,
    }));
  }

  // Build permissions
  const permissions: UserPermissions =
    (profile.permissions as UserPermissions) || { ...DEFAULT_PERMISSIONS };

  return {
    id,
    name,
    email,
    role,
    avatar,
    permissions,
    organizations: organizations.length > 0 ? organizations : undefined,
    status:
      (profile.status as 'Active' | 'Inactive' | 'Suspended' | 'Pending') ||
      undefined,
    created_at: (profile.created_at as string) || undefined,
    last_login: (profile.last_login as string) || undefined,
  };
}

/**
 * Local Auth Adapter
 *
 * Provides authentication operations via direct API calls to cfleet-core-server.
 * Uses bearer token authentication stored in localStorage/SecureStore.
 *
 * @example
 * ```ts
 * const adapter = new LocalAuthAdapter(
 *   'http://localhost:8081/api/v1',
 *   APP_AUTH_CONFIGS.dispatcher
 * );
 *
 * // Login
 * const response = await adapter.login({ email, password });
 * // response.tokens.accessToken contains the bearer token
 *
 * // Refresh
 * const refreshed = await adapter.refreshSession(response.tokens.refreshToken);
 *
 * // Logout
 * await adapter.logout(response.tokens.accessToken);
 * ```
 */
export class LocalAuthAdapter implements AuthAdapter {
  readonly name = 'local';
  readonly usesCookies = false;

  /**
   * Create a new Local Auth adapter
   * @param baseUrl - Base URL for API requests (e.g., 'http://localhost:8081/api/v1')
   * @param config - App-specific auth configuration
   */
  constructor(
    private readonly baseUrl: string,
    private readonly config: AppAuthConfig
  ) {}

  /**
   * Authenticate with email/password
   * Uses the app-configured login endpoint
   */
  async login(credentials: LoginCredentials): Promise<AuthAdapterResponse> {
    // Build request body with app-specific fields
    const requestBody: Record<string, unknown> = {
      email: credentials.email,
      password: credentials.password,
    };

    // Add customer-specific remember_me field
    if (this.config.appType === 'customer' && credentials.rememberMe !== undefined) {
      requestBody.remember_me = credentials.rememberMe;
    }

    // Add driver-specific device_id field
    if (this.config.appType === 'driver' && credentials.deviceId) {
      requestBody.device_id = credentials.deviceId;
    }

    const response = await fetch(`${this.baseUrl}${this.config.endpoints.login}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || errorData.message || `Login failed: ${response.status}`
      );
    }

    const data = await response.json();
    return this.normalizeLoginResponse(data);
  }

  /**
   * Sign out and invalidate session
   * Uses the app-configured logout endpoint with bearer token
   */
  async logout(accessToken: string | null): Promise<void> {
    if (!accessToken) {
      return; // No token, nothing to logout
    }

    try {
      await fetch(`${this.baseUrl}${this.config.endpoints.logout}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch {
      // Ignore logout failures - session may already be invalid
      console.warn('[LocalAuthAdapter] Logout request failed');
    }
  }

  /**
   * Refresh the access token
   * Uses the app-configured refresh endpoint
   */
  async refreshSession(refreshToken: string | null): Promise<AuthAdapterResponse> {
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseUrl}${this.config.endpoints.refresh}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    return this.normalizeRefreshResponse(data, refreshToken);
  }

  /**
   * Get current session - not supported for local auth
   * Local auth uses stored tokens, not session cookies
   */
  async getSession(): Promise<SessionResponse | null> {
    // Local auth doesn't support session endpoint
    // Session state is managed via stored tokens
    return null;
  }

  /**
   * Sign up is not directly supported by local auth adapter
   * Sign up goes through separate API endpoints
   */
  async signUp(
    _email: string,
    _password: string,
    _name: string,
    _additionalData?: Record<string, unknown>
  ): Promise<AuthAdapterResponse> {
    throw new Error(
      'Sign up not supported via LocalAuthAdapter. Use direct API call to signup endpoint.'
    );
  }

  /**
   * Request password reset - not implemented in adapter
   * Uses direct API endpoint
   */
  async forgotPassword(_email: string): Promise<void> {
    throw new Error(
      'Forgot password not supported via LocalAuthAdapter. Use direct API call.'
    );
  }

  /**
   * Reset password - not implemented in adapter
   * Uses direct API endpoint
   */
  async resetPassword(_token: string, _newPassword: string): Promise<void> {
    throw new Error(
      'Reset password not supported via LocalAuthAdapter. Use direct API call.'
    );
  }

  /**
   * Normalize login response to AuthAdapterResponse
   */
  private normalizeLoginResponse(data: Record<string, unknown>): AuthAdapterResponse {
    // Parse tokens using config-specified field names
    const accessToken = data[this.config.accessTokenField] as string;
    const refreshToken = (data.refresh_token as string) || '';
    const expiresIn = (data.expires_in as number) || 3600;

    if (!accessToken) {
      throw new Error(
        `Invalid login response: missing ${this.config.accessTokenField} field`
      );
    }

    const tokens: AuthTokens = {
      accessToken,
      refreshToken,
      expiresIn,
      expiresAt: Date.now() + expiresIn * 1000,
    };

    // Parse profile using config-specified field name
    const rawProfile = data[this.config.profileField] as Record<string, unknown>;
    if (!rawProfile) {
      throw new Error(
        `Invalid login response: missing ${this.config.profileField} field`
      );
    }

    const user = normalizeProfile(rawProfile, this.config.appType);
    const organizations = user.organizations || [];

    return {
      user,
      tokens,
      organizations,
    };
  }

  /**
   * Normalize refresh response to AuthAdapterResponse
   * Note: Refresh response may not include user profile
   */
  private normalizeRefreshResponse(
    data: Record<string, unknown>,
    existingRefreshToken: string
  ): AuthAdapterResponse {
    // Parse tokens
    const accessToken = data[this.config.accessTokenField] as string;
    const newRefreshToken = (data.refresh_token as string) || existingRefreshToken;
    const expiresIn = (data.expires_in as number) || 3600;

    if (!accessToken) {
      throw new Error(
        `Invalid refresh response: missing ${this.config.accessTokenField} field`
      );
    }

    const tokens: AuthTokens = {
      accessToken,
      refreshToken: newRefreshToken,
      expiresIn,
      expiresAt: Date.now() + expiresIn * 1000,
    };

    // Refresh response typically doesn't include user, create minimal user
    // The caller should merge with existing user data
    const user: User = {
      id: '',
      name: '',
      email: '',
      role: 'Dispatcher',
      permissions: { ...DEFAULT_PERMISSIONS },
    };

    return {
      user,
      tokens,
      organizations: [],
    };
  }
}
